import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { userService } from "../services/user.service";
import { utils } from "../helpers/utils";

interface User {
  id: string;
  email: string;
  name: string;
  username?: string;
  avatar?: string;
  role?: string;
  classrooms?: Classroom[];
  // Add other user properties as needed
}

interface Classroom {
  id: number;
  name: string;
  levels: any[];
}

interface UserState {
  id: string | null | undefined;
  user: User | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  id: "",
  user: null,
  loading: false,
  error: null,
};

// get user profile using userService
export const getUserProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  "/api/profile/me",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await userService.getProfile();
      dispatch(setUser(response));
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to get user profile");
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk<
  boolean,
  void,
  { rejectValue: string }
>("user/logout", async (_, { rejectWithValue }) => {
  try {
    await userService.logout()
    return true;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to logout");
  }
});

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearUser: (state) => {
      state.user = null;
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Handle logoutUser
    builder
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearUser, setUser } =
  userSlice.actions; 

export default userSlice.reducer;
